import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Search, Plus, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import SEOMeta from '../components/SEOMeta';

const speciesEmoji = { cane: '🐕', gatto: '🐈', coniglio: '🐇', uccello: '🐦', altro: '🐾' };

// Stelle animate per il cielo notturno del Giardino
const GIARDINO_STARS = [
  {l:'4%', t:'12%',s:2,d:3.2,dl:0   },{l:'10%',t:'58%',s:1,d:4.5,dl:0.8 },
  {l:'16%',t:'28%',s:3,d:3.8,dl:1.5 },{l:'23%',t:'80%',s:1,d:5.0,dl:0.3 },
  {l:'30%',t:'45%',s:2,d:3.5,dl:2.1 },{l:'36%',t:'90%',s:1,d:4.2,dl:0.6 },
  {l:'43%',t:'18%',s:2,d:3.9,dl:1.3 },{l:'49%',t:'68%',s:3,d:4.8,dl:0.1 },
  {l:'55%',t:'35%',s:1,d:3.3,dl:1.9 },{l:'62%',t:'82%',s:2,d:4.1,dl:2.4 },
  {l:'68%',t:'22%',s:2,d:3.6,dl:0.7 },{l:'74%',t:'52%',s:1,d:5.0,dl:1.1 },
  {l:'80%',t:'10%',s:3,d:4.4,dl:0.4 },{l:'86%',t:'70%',s:1,d:3.7,dl:2.2 },
  {l:'92%',t:'38%',s:2,d:4.6,dl:0.9 },{l:'97%',t:'88%',s:1,d:3.1,dl:1.7 },
  {l:'8%', t:'92%',s:2,d:4.9,dl:0.2 },{l:'27%',t:'60%',s:1,d:3.4,dl:2.8 },
  {l:'50%',t:'50%',s:2,d:4.7,dl:0.5 },{l:'76%',t:'94%',s:1,d:3.8,dl:1.4 },
  {l:'14%',t:'40%',s:2,d:4.3,dl:1.6 },{l:'38%',t:'25%',s:3,d:3.2,dl:2.6 },
  {l:'63%',t:'75%',s:2,d:4.0,dl:0.3 },{l:'88%',t:'48%',s:1,d:3.5,dl:1.8 },
  {l:'2%', t:'70%',s:3,d:4.2,dl:0.0 },{l:'52%',t:'5%', s:2,d:3.9,dl:3.0 },
  {l:'70%',t:'15%',s:1,d:4.6,dl:0.7 },{l:'20%',t:'8%', s:2,d:3.3,dl:1.0 },
];

export default function GiardinoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [memorials, setMemorials] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [species, setSpecies] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchMemorials = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: '12' });
      if (species) params.append('species', species);
      if (search) params.append('search', search);
      const r = await api.get(`/memorials?${params}`);
      setMemorials(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.pages || 1);
    } catch {} finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMemorials(); }, [species, page]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchMemorials(); };

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Il Giardino delle Stelle"
        description="Il Giardino delle Stelle — il cimitero digitale di Una Zampa in Paradiso. Onora la memoria del tuo amico a quattro zampe, accendi una candela e lascia un messaggio d'amore."
        url="/giardino-delle-stelle"
      />
      {/* Hero — Cielo notturno del Giardino delle Stelle */}
      <section className="relative garden-gradient min-h-[65vh] flex items-center overflow-hidden">
        {/* Campo stelle twinkling */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {GIARDINO_STARS.map((s, i) => (
            <div
              key={i}
              className="star-pt"
              style={{ left: s.l, top: s.t, width: s.s + 'px', height: s.s + 'px', '--dur': s.d + 's', '--del': s.dl + 's' }}
            />
          ))}
          {/* Stelle cadenti */}
          <div className="shooting-star" style={{ left: '15%', top: '20%', '--dur': '2.2s', '--del': '3s'  }} />
          <div className="shooting-star" style={{ left: '55%', top: '10%', '--dur': '1.8s', '--del': '8s'  }} />
          <div className="shooting-star" style={{ left: '70%', top: '5%',  '--dur': '2.0s', '--del': '13s' }} />
        </div>
        {/* Nebulosa verde sottile al centro */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] orb-float opacity-20 pointer-events-none"
          style={{ background: 'var(--color-sage)', filter: 'blur(90px)', '--dur': '15s', '--del': '0s' }}
          aria-hidden="true"
        />
        <div className="container-custom relative z-10 py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-widest mb-5">
              <Star size={14} fill="currentColor" className="twinkle" /> Cimitero online
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-white mb-5 leading-tight">{t('garden.title')}</h1>
            <p className="text-white/65 text-lg max-w-xl leading-relaxed">{t('garden.subtitle')}</p>
            <div className="flex items-center gap-3 mt-8">
              {user ? (
                <Button
                  className="bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl px-6 btn-press shadow-lg"
                  onClick={() => navigate('/area-clienti')}
                  data-testid="create-memorial-button"
                >
                  <Plus size={16} className="mr-2" /> {t('garden.create')}
                </Button>
              ) : (
                <Button
                  className="bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl px-6 btn-press shadow-lg"
                  onClick={() => navigate('/registrati')}
                  data-testid="create-memorial-button"
                >
                  <Plus size={16} className="mr-2" /> {t('garden.create')}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats bar */}
      <div className="bg-[var(--color-paper)] border-b border-[var(--color-border)] py-4">
        <div className="container-custom flex items-center justify-center gap-8 text-sm">
          <div className="flex items-center gap-2 text-[var(--color-muted-text)]">
            <Star size={14} className="text-[var(--color-terracotta)]" fill="currentColor" />
            <span className="font-semibold text-[var(--color-deep)]">{total}</span> profili nel Giardino
          </div>
          <div className="flex items-center gap-2 text-[var(--color-muted-text)]">
            <Flame size={14} className="text-[var(--color-terracotta)]" />
            <span className="font-semibold text-[var(--color-deep)]">{memorials.reduce((s, m) => s + (m.candles || 0), 0)}</span> candele accese
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <section className="py-6 bg-[var(--color-cream)]">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-text)]" />
                <Input
                  placeholder={t('garden.search')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 border-[var(--color-border)] bg-[var(--color-paper)]"
                  data-testid="garden-search-input"
                />
              </div>
              <Button type="submit" variant="outline" className="border-[var(--color-border)]">Cerca</Button>
            </form>
            <Select value={species} onValueChange={v => { setSpecies(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-44 border-[var(--color-border)] bg-[var(--color-paper)]" data-testid="species-filter">
                <SelectValue placeholder={t('garden.all_species')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('garden.all_species')}</SelectItem>
                <SelectItem value="cane">{speciesEmoji.cane} {t('garden.species.cane')}</SelectItem>
                <SelectItem value="gatto">{speciesEmoji.gatto} {t('garden.species.gatto')}</SelectItem>
                <SelectItem value="coniglio">{speciesEmoji.coniglio} {t('garden.species.coniglio')}</SelectItem>
                <SelectItem value="uccello">{speciesEmoji.uccello} {t('garden.species.uccello')}</SelectItem>
                <SelectItem value="altro">{speciesEmoji.altro} {t('garden.species.altro')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Memorials Grid */}
      <section className="pb-16">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner" /></div>
          ) : memorials.length === 0 ? (
            <div className="text-center py-20">
              <Star size={40} className="mx-auto text-[var(--color-stone)] mb-4" />
              <p className="text-[var(--color-muted-text)]">{t('garden.no_memorials')}</p>
              <Button className="mt-4 bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white" onClick={() => navigate('/registrati')}>Crea il primo memorial</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="memorials-grid">
              {memorials.map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={`/giardino-delle-stelle/${m.id}`}
                    className="card-hover block bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-sm group"
                    data-testid="memorial-card"
                  >
                    <div className="h-44 overflow-hidden relative">
                      <img
                        src={m.photo_url || 'https://images.unsplash.com/photo-1559190394-df5a28aab5c5?w=300&q=60'}
                        alt={m.pet_name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {/* Warm cinematic overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                      <div className="absolute top-2 right-2 bg-[var(--color-paper)]/90 backdrop-blur-sm rounded-full px-2 py-1 text-xs flex items-center gap-1">
                        <span>{speciesEmoji[m.species] || '🐾'}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-serif font-semibold text-[var(--color-deep)] text-lg">{m.pet_name}</h4>
                      {m.breed && <p className="text-xs text-[var(--color-muted-text)]">{m.breed}</p>}
                      {m.death_date && (
                        <p className="text-xs text-[var(--color-muted-text)] mt-1">
                          {t('garden.died')} {new Date(m.death_date).toLocaleDateString('it-IT')}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--color-border)] group-hover:border-amber-200/40 transition-colors duration-300">
                        <div className="flex items-center gap-1 text-xs">
                          <Flame size={12} className="text-[var(--color-terracotta)] group-hover:text-amber-500 transition-colors duration-300" />
                          <span className="font-semibold text-[var(--color-deep)]" data-testid={`candle-count-${m.id}`}>{m.candles || 0}</span>
                          <span className="text-[var(--color-muted-text)]">{t('garden.candles')}</span>
                        </div>
                        <span className="text-xs text-[var(--color-muted-text)] ml-auto">{m.owner_name}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-10">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-xl text-sm font-medium transition-colors ${
                  page === p ? 'bg-[var(--color-sage)] text-white' : 'bg-[var(--color-paper)] border border-[var(--color-border)] hover:border-[var(--color-sage)]'
                }`}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
